// Dok 18 – Projektörens arbetsmiljörutin.
// Beskriver hur projektören uppfyller sitt ansvar enligt AFS 2023:3 kap. 2,
// och kan lämnas till ett projekt som inte använder systemet.

export interface DesignerSectionDef {
  key: string;
  title: string;
  hint: string;
  /** Förifylld standardtext som projektören kan justera. */
  template: string;
}

export const DESIGNER_ROUTINE_SECTIONS: DesignerSectionDef[] = [
  {
    key: "syfte",
    title: "1. Syfte och omfattning",
    hint: "Vilket uppdrag, vilka discipliner och vilka skeden rutinen gäller för.",
    template:
      "Rutinen beskriver hur arbetsmiljöfrågor hanteras i projekteringsuppdraget: vilka discipliner som omfattas, i vilka skeden arbetet sker och hur resultatet redovisas till byggherren och till byggarbetsmiljösamordnaren för planering och projektering (Bas-P). Rutinen gäller från uppdragsstart till dess att handlingarna är överlämnade och kvarvarande risker är redovisade.",
  },
  {
    key: "lagrum",
    title: "2. Lagrum och efterlevnad",
    hint: "Vilka regler projektörsansvaret bygger på.",
    template:
      "Arbetet utgår från arbetsmiljölagen 3 kap. 7 a § samt Arbetsmiljöverkets föreskrifter:\nAFS 2023:3 kap. 2 – den som projekterar ska inom sitt ansvarsområde se till att arbetsmiljön beaktas för både byggskedet och bruksskedet.\nAFS 2023:12 – risker vid byggnads- och anläggningsarbete.\nAFS 2023:11 – arbetsplatsens utformning i bruksskedet.\nELSÄK-FS 2022:1 – tillämpas när elanläggningar ingår i uppdraget.\nRutinen ska visa hur dessa krav uppfylls och hur efterlevnaden kan följas i efterhand.",
  },
  {
    key: "arbetssatt",
    title: "3. Arbetssätt steg för steg",
    hint: "Från riskidentifiering till redovisad kvarvarande risk.",
    template:
      "1. Förutsättningar samlas in: arbetsområdets förhållanden utreds (Dok 10) tillsammans med underlag från byggherren.\n2. Risker identifieras löpande i projekteringen, disciplinvis och vid varje handlingsskede.\n3. Risker åtgärdas i första hand genom ändrad projektering – byggs bort i handlingen.\n4. Risker som inte kan byggas bort dokumenteras som kvarvarande risk för byggskedet respektive bruksskedet med förslag till skyddsåtgärd.\n5. Kvarvarande risker redovisas till Bas-P och byggherren i projektörsinlämningen (Dok 16) och i risksammanställningarna (Dok 30b och Dok 31).\n6. Leveransen sammanfattas i ett leveransintyg (Dok 19) med datum, version och undertecknande.",
  },
  {
    key: "skeden",
    title: "4. Frågor som inte kan avgöras i aktuellt skede",
    hint: "Hur en fråga skjuts fram med beslut och motiv.",
    template:
      "Om en fråga inte kan avgöras i aktuellt skede skjuts den fram till ett senare skede genom ett dokumenterat beslut. Beslutet innehåller vem som flyttat frågan, till vilket skede, motiv samt vem som ansvarar för att den hanteras. Framskjutna frågor följer med i leveransen och redovisas öppet, så att ingen fråga kan falla mellan skedena.",
  },
  {
    key: "granskning",
    title: "5. Granskning, egenkontroll och kommunikation",
    hint: "Hur arbetet kvalitetssäkras och hur dialogen med Bas-P sker.",
    template:
      "Handlingarna egenkontrolleras före leverans och arbetsmiljörisker är en fast punkt vid projekteringsmöten. Frågor och svar som påverkar arbetsmiljön loggas (Dok 25) så att svar går att spåra. Uppdragsledare eller motsvarande granskar leveransen innan den lämnas. Om mottagaren begär komplettering hanteras den som en ny version av leveransen.",
  },
  {
    key: "dokumentation",
    title: "6. Dokumentation, version och revision",
    hint: "Hur spårbarheten säkerställs.",
    template:
      "Samtliga bedömningar, beslut och åtgärder dokumenteras med datum, ansvarig person, skede och version. Varje exporterat dokument får en unik verifieringskod som mottagaren kan kontrollera. Vid ändrade förutsättningar upprättas en ny version med revisionsnot, och tidigare versioner sparas så att historiken kan följas.",
  },
  {
    key: "leverans",
    title: "7. Leverans till projekt som inte använder systemet",
    hint: "Vad mottagaren får och hur det ska läsas.",
    template:
      "Leveransen lämnas som ett samlat paket i Word-format: leveransintyg (Dok 19), denna rutin (Dok 18), arbetsområdesutredning (Dok 10), projektörsinlämning per disciplin (Dok 16) samt risksammanställning för utförande (Dok 30b) och bruksskede (Dok 31). Mottagaren behöver inget konto i systemet. Bas-P i det mottagande projektet för in de kvarvarande riskerna i sin risksammanställning och arbetsmiljöplan. Vid ändring lämnas en ny version av hela paketet.",
  },
];

export type DesignerRoutineStatus = "utkast" | "granskad" | "faststalld";

export const DESIGNER_ROUTINE_STATUS_LABELS: Record<DesignerRoutineStatus, string> = {
  utkast: "Utkast",
  granskad: "Granskad",
  faststalld: "Fastställd",
};

/** Standardtext i försäkran på leveransintyget (Dok 19). */
export const DESIGNER_DECLARATION_TEMPLATE =
  "Undertecknad intygar att arbetsmiljöfrågor har beaktats i projekteringen enligt arbetsmiljölagen 3 kap. 7 a § och AFS 2023:3 kap. 2, att risker i första hand har byggts bort i handlingarna, samt att de risker som kvarstår för byggskedet och bruksskedet är redovisade i bifogade underlag. Uppgifterna gäller det egna ansvarsområdet och de handlingar som anges i leveransen.";

export const DESIGNER_CERTIFICATE_STATUS_LABELS: Record<string, string> = {
  utkast: "Utkast",
  levererad: "Levererad",
  godkand: "Godkänd av mottagare",
};
